<template>
    <div class="carouselBox" :class="className">
        <div v-if="title && products.length" class="carouselBox__header">
            <h2 v-if="url" class="carouselBox__title">
                <a :href="url" class="carouselBox__title-link">{{ title }}</a>
            </h2>
            <h2 v-else class="carouselBox__title">
                {{ title }}
                <span v-if="stickerTitle" class="carouselBox__title-sticker">{{ stickerTitle }}</span>
            </h2>
            <div v-if="url">
                <a :href="url" class="btn btn-outline-primary btn-thin text-nowrap">
                    {{ ctaText || $t('header.showText') }}
                </a>
            </div>
        </div>

        <h3 v-if="subtitle" class="carouselBox__subtitle" v-html="subtitle"></h3>

        <div class="carouselBox__container" :class="`${bannerImageUrl ? 'carouselBox__container--with-banner' : ''}`">
            <div v-if="bannerImageUrl" class="carouselBox__banner">
                <div class="carouselBox__banner__background" :style="`background-image: url('${bannerImageUrl}');`"></div>
                <a :href="url">
                    <img :src="bannerImageUrl" alt="Banner Image" />
                </a>
            </div>
            <div class="carouselBox__carousel">
                <Carousel
                    v-if="products"
                    :products="products"
                    :type-item="typeItem"
                    :show-pre-basket="showPreBasket"
                    :item-list-name="itemListName"
                    :carousel-name="title"
                    :minimalized-product-visual="minimalizedProductVisual"
                    :min-products-arrow-slider-count="minProductsArrowSliderCount"
                    :show-availability="showAvailability"
                    :addon-sale-behavior="addonSaleBehavior"
                />
            </div>
        </div>
        <div v-if="!title && url && products.length" class="text-center mb-4 mt-2">
            <a :href="url" class="btn btn-outline-primary">
                {{ ctaText || $t('header.showText') }}
            </a>
        </div>
    </div>
</template>

<script lang="ts">
import Carousel from '~/components/elements/Carousel.vue'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import { transformProductsToLuigisBoxItems } from '~/utils/transformProductsToLuigisBoxItems'
import Product from '~/models/Product/Product'

export default {
    components: {
        Carousel,
    },
    props: {
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        url: {
            type: String,
            default: '',
        },
        ctaText: {
            type: String,
            default: '',
        },
        bannerImageUrl: {
            type: String,
            default: '',
        },
        className: {
            type: String,
            default: '',
        },
        products: {
            type: Array as PropType<Product[]>,
            required: true,
        },
        typeItem: {
            type: String,
            default: 'slim-carousel__item--product',
        },
        showPreBasket: {
            type: Boolean,
            default: true,
        },
        itemListName: {
            type: String,
            default: '',
        },
        refreshFrequentlyActualizedData: {
            type: Boolean,
            default: false,
        },
        minimalizedProductVisual: {
            type: Boolean,
            default: false,
        },
        luigisBoxCarouselType: {
            type: String,
            default: '',
        },
        luigisBoxItemIds: {
            type: String,
            default: '',
        },
        isLuigisBoxVariant: {
            type: Boolean,
            default: false,
        },
        stickerTitle: {
            type: String,
            default: '',
        },
        minProductsArrowSliderCount: {
            type: Number,
            default: 5,
        },
        showAvailability: {
            type: Boolean,
            default: true,
        },
        addonSaleBehavior: {
            type: Boolean,
            default: false,
        },
    },

    mounted() {
        if (this.refreshFrequentlyActualizedData) {
            refreshFrequentlyActualizedData(this.products)
        }

        if (this.luigisBoxCarouselType !== '') {
            LuigisBoxEventApi.sendRecommendationEvent(
                transformProductsToLuigisBoxItems(this.products),
                this.title,
                this.luigisBoxCarouselType,
                this.luigisBoxItemIds,
                this.isLuigisBoxVariant
            )
        }
    },
}
</script>

<style lang="scss" scoped>
/*
Structure:
    .carouselBox(.carouselBox--fullWidth|.carouselBox--dealDetail)
        .carouselBox__title
            .carouselBox__title-link
 */

.carouselBox {
    &--fullWidth {
        width: calc(100% + (2 * $padding-container-inside-mobile));
        margin-left: -$padding-container-inside-mobile;
        margin-bottom: 40px;

        @media only screen and (min-width: 576px) {
            width: calc(100% + (2 * $padding-container-inside));
            margin-left: -$padding-container-inside;
        }

        &:deep(.carouselBox__title) {
            margin-left: $padding-container-inside-mobile;

            @media (min-width: $max-width-container-inside) {
                margin-left: 6px;
            }
        }

        &:deep(.carouselBox__subtitle) {
            margin-left: $padding-container-inside-mobile;

            @media (min-width: $max-width-container-inside) {
                margin-left: 6px;
            }
        }

        @media (min-width: $max-width-container-inside) {
            width: calc(100% + 12px); //margin on .product
            margin-left: -6px;
        }
    }

    &--dealDetail {
        .slim-carousel__item--product {
            @media (min-width: 992px) {
                flex: 0 0 calc(100% / 3.5);
                width: calc(100% / 3.5);
            }
            @media (min-width: $max-width-container-inside) {
                flex: 0 0 calc(100% / 4.5);
                width: calc(100% / 4.5);
            }
        }
    }

    &__container {
        &--with-banner {
            border: 1px solid $color-grey-dark;
            border-radius: 20px;
            margin: 5px 0px 20px;

            @include screen-md {
                display: grid;
                grid-template-columns: minmax(0, min(450px, 50%)) 1fr;
                border: 0;
                border-radius: 20px;
                margin: 5px 6px 20px;
            }

            .carouselBox__carousel {
                padding: 15px 7px 0px 0;

                @include screen-md {
                    border: 1px solid $color-grey-dark;
                    border-left: 0;
                    border-radius: 0 20px 20px 0;
                    padding: 7px 10px 0px 7px;
                }
            }
        }
    }

    &__header {
        display: flex;
        justify-content: start;
        gap: 20px;
        align-items: center;
        padding-right: 10px;
    }

    &__banner {
        border-radius: 20px 20px 0 0;
        height: 100%;
        align-items: center;
        display: flex;
        position: relative;
        overflow: hidden;

        &__background {
            position: absolute;
            z-index: 0;
            content: '';
            background: center center / cover no-repeat;
            filter: blur(5px);
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
        }

        img {
            position: relative;
            z-index: 1;
        }

        @include screen-md {
            border-radius: 20px 0 0 20px;
        }
    }

    &__title-sticker {
        background: $color-green-light;
        color: $color-green-dark;
        border-radius: 20px;
        display: inline-block;
        padding: 7px 20px;
        font-size: 14px;
        line-height: 20px;
    }
}

.carouselBox__title {
    font-size: 20px;
    line-height: 26px;
    color: $color-primary-green;
    text-align: left;
    margin-bottom: 0;
    font-family: 'scale-variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans',
        'Droid Sans', 'Helvetica Neue', sans-serif;
    font-variation-settings: 'wght' 500, 'wdth' 110;

    @media (max-width: 359px) {
        font-size: 18px;
        line-height: 24px;
    }

    @include screen-md {
        font-size: 28px;
        line-height: 30px;
    }
}

.carouselBox__subtitle {
    font-size: 18px;
    line-height: 28px;
    color: $color-grey-800;
    text-align: left;
    margin-bottom: 0;
    font-family: 'scale-variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans',
        'Droid Sans', 'Helvetica Neue', sans-serif;
    font-variation-settings: 'wght' 500, 'wdth' 100;
    margin-top: 5px;

    @include screen-md {
        font-size: 20px;
        line-height: 24px;
    }
}

.carouselBox__title-link {
    color: $color-primary-green;
}

.btn {
    @media (max-width: 359px) {
        padding: 5px 10px;
    }
}
</style>
